import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Users, 
  FileCheck, 
  Lock, 
  ArrowRight,
  Layers,
  CheckCircle2,
  Building2,
  Plane,
  Heart,
  Truck,
  FileText
} from 'lucide-react';

// Section wrapper for consistent spacing
const Section = ({ children, className = '' }) => (
  <section className={`py-16 ${className}`}>
    <div className="max-w-4xl mx-auto px-6">
      {children}
    </div>
  </section>
);

// CAR Model Card
const CARCard = ({ icon: Icon, title, description }) => (
  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 bg-cyan-500/10 rounded-lg">
        <Icon className="w-5 h-5 text-cyan-400" />
      </div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
    </div>
    <p className="text-slate-400 leading-relaxed">{description}</p>
  </div>
);

// Governance Layer
const GovernanceLayer = ({ number, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
      <span className="text-sm font-semibold text-cyan-400">{number}</span>
    </div>
    <div className="flex-1 pb-8 border-l border-slate-700 pl-6 -ml-4">
      <h4 className="text-white font-medium mb-2">{title}</h4>
      <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

// Reuse Example
const ReuseExample = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-slate-800 rounded-lg">
      <Icon className="w-4 h-4 text-slate-400" />
    </div>
    <div>
      <h4 className="text-white font-medium text-sm">{title}</h4>
      <p className="text-slate-500 text-sm">{description}</p>
    </div>
  </div>
);

const KatlasGovernance = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800">
      {/* Navigation */}
      <nav className="border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-slate-400 hover:text-white text-sm flex items-center gap-2">
            ← Back to Dashboard
          </Link>
          <Link 
            to="/passenger/assisted" 
            className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-2"
          >
            View Demo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <Section className="pt-20 pb-12">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-6">
            <Layers className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm">Architecture Explainer</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            KATLAS Governance Substrate
          </h1>
          
          <p className="text-xl text-slate-400 mb-8">
            The run-time operational governance layer beneath AI-assisted workflows
          </p>
          
          <p className="text-slate-400 leading-relaxed max-w-3xl mx-auto">
            The Assisted Travel Advisor demonstrates a user-facing journey in which a 
            vulnerable passenger receives governed recommendations during service disruption. 
            KATLAS provides the governance substrate beneath that experience—the layer that 
            makes requests, approvals, handoffs, and exceptions trustworthy across the actors 
            involved in real-time operations.
          </p>
        </div>
      </Section>

      {/* Divider */}
      <div className="max-w-4xl mx-auto px-6">
        <div className="border-t border-slate-800" />
      </div>

      {/* Why This Matters */}
      <Section>
        <h2 className="text-2xl font-semibold text-white mb-6">Why this matters</h2>
        
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
          <p className="text-slate-300 leading-relaxed">
            In multi-actor workflows, the issue is not only what an AI recommends. 
            The harder question is what may be <span className="text-white">requested</span>, 
            {' '}<span className="text-white">reviewed</span>, <span className="text-white">shared</span>, 
            {' '}<span className="text-white">approved</span>, <span className="text-white">denied</span>, 
            {' '}<span className="text-white">escalated</span>, or <span className="text-white">acted upon</span>—in 
            real time, across organisational boundaries, with appropriate authority and 
            accountable evidence at each step.
          </p>
          <p className="text-slate-400 leading-relaxed mt-4">
            Post-event audit cannot substitute for run-time governance. KATLAS governs 
            execution as it happens, producing receipts as part of the governed process 
            rather than reconstructing accountability afterwards.
          </p>
        </div>
      </Section>

      {/* CAR Model */}
      <Section className="bg-slate-800/20">
        <h2 className="text-2xl font-semibold text-white mb-3">The CAR Model</h2>
        <p className="text-slate-400 mb-8">
          Three principles structure how KATLAS governs live operations.
        </p>
        
        <div className="grid md:grid-cols-3 gap-4">
          <CARCard 
            icon={Lock}
            title="Custody"
            description="Sensitive data remains with the responsible role or organisation. Information is not centralised or moved unless there is a governed reason to disclose it to another actor."
          />
          <CARCard 
            icon={Shield}
            title="Authority"
            description="Requests, review, sharing, and action are governed at run time by role, purpose, and permission. Authority is checked before or during action, not asserted afterwards."
          />
          <CARCard 
            icon={FileCheck}
            title="Receipts"
            description="Each material handoff generates attributable evidence of request, response, and outcome. Receipts are produced as part of the governed process, not bolted on after the fact."
          />
        </div>
      </Section>

      {/* Six-Layer Governance Substrate */}
      <Section>
        <h2 className="text-2xl font-semibold text-white mb-3">Six-Layer Governance Substrate</h2>
        <p className="text-slate-400 mb-10">
          KATLAS provides a layered architecture for operational governance across distributed actors.
        </p>
        
        <div className="space-y-0">
          <GovernanceLayer 
            number="1"
            title="Role Identity & Governed Custody"
            description="Each actor has a verifiable role identity. Data custody is explicit: the organisation or role responsible for information retains it unless disclosure is governed and necessary."
          />
          <GovernanceLayer 
            number="2"
            title="Authority, Purpose & Operational Boundaries"
            description="What each role may request, review, approve, or act upon is bounded by authority grants and purpose constraints. AI-assisted actions operate within the same boundaries as human actors in equivalent roles."
          />
          <GovernanceLayer 
            number="3"
            title="Run-Time Decision Gate"
            description="Before an action proceeds, the governance layer checks authority, scope, and workflow state. Requests outside scope are blocked or escalated—not logged after the fact."
          />
          <GovernanceLayer 
            number="4"
            title="Governed Interaction & Handoffs"
            description="When one actor hands responsibility to another, the handoff is governed: minimum necessary disclosure, explicit acknowledgment, recorded state. The receiving actor can trust that prior steps were properly completed."
          />
          <GovernanceLayer 
            number="5"
            title="Attributable Receipts & Operational Lineage"
            description="Each governed action produces a signed receipt linking actor, authority, input summary, decision, and outcome. Receipts form an operational lineage that can be reviewed without reconstructing events from scattered logs."
          />
          <GovernanceLayer 
            number="6"
            title="Assurance, Exception Review & Reuse"
            description="Receipts support exception review, assurance checks, and evidence for regulatory inquiry. The same governance pattern can be reused across similar workflows without rebuilding controls from scratch."
          />
        </div>
      </Section>

      {/* How This Supports the Demo */}
      <Section className="bg-slate-800/20">
        <h2 className="text-2xl font-semibold text-white mb-3">
          How this supports the Assisted Travel demo
        </h2>
        <p className="text-slate-400 mb-8">
          A governed episode in the assisted journey:
        </p>
        
        <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-300">
                A passenger with reduced mobility requests assistance for a journey 
                affected by service disruption.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-300">
                The relevant operational actor checks scope and authority—can this 
                request be handled, and does the actor have permission to access 
                the necessary information?
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-300">
                Only the minimum necessary information is shared with downstream 
                actors. Personal details remain with the responsible custodian.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-300">
                Readiness or exception is recorded. If assistance cannot be 
                provided as requested, the exception is captured with reason and 
                escalation path.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-300">
                The next actor in the workflow can rely on the handoff—prior steps 
                are evidenced, not assumed.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-300">
                Accountable evidence is produced: a signed receipt linking request, 
                authority, decision, and outcome.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Horizontal Reuse */}
      <Section>
        <h2 className="text-2xl font-semibold text-white mb-3">A reusable governance pattern</h2>
        <p className="text-slate-400 mb-8">
          The same substrate can support other workflows where authority is distributed, 
          handoffs matter, and sensitive data should not be moved unnecessarily.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <ReuseExample 
            icon={Plane}
            title="Assisted Travel"
            description="Governing passenger assistance requests, staff coordination, and exception handling across operators"
          />
          <ReuseExample 
            icon={Heart}
            title="Patient Pathways"
            description="Governing referrals, care handoffs, and sensitive information sharing across healthcare providers"
          />
          <ReuseExample 
            icon={Truck}
            title="Freight & Customs"
            description="Governing cargo handoffs, documentation checks, and clearance decisions across borders and jurisdictions"
          />
          <ReuseExample 
            icon={Building2}
            title="Regulated Case Management"
            description="Governing case assignment, review authority, and decision accountability in public-service workflows"
          />
        </div>
        
        <p className="text-slate-500 text-sm mt-8">
          In each case, the pattern is the same: bounded authority, governed handoffs, 
          minimum disclosure, and attributable receipts produced as part of execution.
        </p>
      </Section>

      {/* Closing */}
      <Section className="pb-20">
        <div className="text-center border-t border-slate-800 pt-16">
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            The demo shows the service experience.<br />
            KATLAS explains how that experience is governed.
          </p>
          
          <Link 
            to="/passenger/assisted"
            className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
          >
            <FileText className="w-4 h-4" />
            View the Assisted Travel Demo
          </Link>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-500 text-sm">
          KATLAS Governance Substrate · Architecture Documentation
        </div>
      </footer>
    </div>
  );
};

export default KatlasGovernance;
